import {
  BonusConfiguration,
  BonusContext,
  BonusScene,
  INumberFormatter,
  NUMBER_FORMATTER_TOKEN,
} from '@cgs/common';
import { NewBonusGame } from '@cgs/features';
import { CancellationTokenSource } from '@cgs/shared';
import { Container } from '@cgs/wge';
import { SlotBonusGameConfigManager } from '../../../new/config/slot-bonus-game-config-manager';
import { SLOT_GAME_CONFIG_MANAGER_TOKEN } from '../../../new/config/slot-game-config-manager';
import { RESOURCES_COMPONENT_TOKEN } from '../../../type_definitions';
import { ResourcesComponent } from '../resources_component';
import { BonusGameProvider } from './bonus_game_provider';
import { T_IStorageRepositoryProvider } from '../storage-repository.provider';

export interface IBonusGameOptions {
  hideSlot: boolean;
  hideHud: boolean;
  gameTypes: string[] | null;
  externalScenesIds: string[] | null;
  gameTypeToBonusId: Map<string, string> | null;
  hasFreeSpins: boolean;
  fsTypes: string[] | null;
}

export class NewBonusGameProvider extends BonusGameProvider {
  private _hasFreeSpins: boolean = false;
  private _fsTypes: string[];
  get fsTypes(): string[] {
    return this._fsTypes;
  }

  private readonly configManager: SlotBonusGameConfigManager;
  private readonly numberFormatter: INumberFormatter;

  constructor(
    container: Container,
    {
      hideSlot = true,
      hideHud = true,
      gameTypes = null,
      externalScenesIds = null,
      gameTypeToBonusId = null,
      hasFreeSpins = false,
      fsTypes = null,
    }: IBonusGameOptions
  ) {
    super(container, hideSlot, hideHud, gameTypes, externalScenesIds, gameTypeToBonusId);
    this._hasFreeSpins = hasFreeSpins;
    this._fsTypes = fsTypes ?? [];

    this.configManager = container.resolve<SlotBonusGameConfigManager>(
      SLOT_GAME_CONFIG_MANAGER_TOKEN
    );
    this.numberFormatter = container.resolve<INumberFormatter>(NUMBER_FORMATTER_TOKEN);
  }

  public async startBonus(): Promise<void> {
    if (
      this.bonusResponse &&
      (!this.gameTypes || this.gameTypes.includes(this.bonusResponse.type))
    ) {
      this.cancellationTokenSource = new CancellationTokenSource();

      super.bonusHolder =
        this.container.resolve<ResourcesComponent>(RESOURCES_COMPONENT_TOKEN).bonusHolder;

      const bonusId = this.gameTypeToBonusId.has(this.bonusResponse.type)
        ? this.gameTypeToBonusId.get(this.bonusResponse.type)!
        : '';

      const config = await this.configManager.getConfig(bonusId);

      const configuration = BonusConfiguration.fromJson(config!, this.numberFormatter);
      const bonusContext = new BonusContext();
      bonusContext.storageRepositoryProvider = this.container.resolve(T_IStorageRepositoryProvider);
      const root = this.container.resolve<ResourcesComponent>(RESOURCES_COMPONENT_TOKEN).root;
      const externalScenes = this.externalScenesIds
        ? this.externalScenesIds.map((id) => root.findById(id)!)
        : null;
      this.bonusScene = new BonusScene(
        this.bonusNode,
        configuration,
        this.resourceManager,
        bonusContext,
        externalScenes,
        this.cancellationTokenSource.token,
        this
      );
      this.bonusGame = new NewBonusGame(
        this.bonusScene,
        configuration,
        bonusContext,
        this.updateBonusFinishPropertiesWithCurrent(),
        this.bonusResponse.type,
        this.cancellationTokenSource.token,
        this.numberFormatter
      );
      this.miniGameCreatedDispatcher.dispatchEvent(this.bonusGame);

      this.serverException = this.bonusGame.onServerException.listen(() => {
        this.serverException.cancel();
        this.onServerExceptionInternal();
      });
      this.finishingSub = this.bonusScene.bonusFinishing.listen(() => {
        this.finishingSub.cancel();
        this.onMiniGameFinishingInternal();
      });
      this.shownSub = this.bonusScene.bonusShown.listen(() => {
        this.shownSub.cancel();
        this.miniGameShownDispatcher.dispatchEvent();
        this.onMiniGameStartedInternal();
      });
      this.finishedSub = this.bonusScene.bonusFinished.listen(() => {
        this.finishedSub.cancel();
        this.onMiniGameFinishedInternal();
      });
      this.updatedSub = this.bonusScene.bonusUpdated.listen(() => {
        this.updatedSub.cancel();
        this.onMiniGameUpdatedInternal();
      });

      this.bonusGame.start(this.bonusResponse);
    }
  }
}
